import { type ComponentProps, type ReactElement, type ReactNode, useEffect, useState } from 'react';

import { OnboardingFlow } from '../../../../backend/src/user/onboarding-flow';
import { useCompleteOnboardingFlow } from '@/api/mutations/useCompleteOnboardingFlow';
import { useUserContext } from '@/Contexts/User';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from '@/new/components/shadcn/popover';
import { cn } from '@/new/lib/utils';

type CoachMarkContentProps = ComponentProps<typeof PopoverContent>;

export interface CoachMarkProps
  extends Omit<CoachMarkContentProps, 'children' | 'content' | 'disableScrollLock' | 'onOpenChange'> {
  /** The element that the coach mark points towards. It must accept a ref. */
  children: ReactElement;
  content: ReactNode;
  flowId: OnboardingFlow;
  delayMs?: number;
  show?: boolean;
  onComplete?: () => void;
}

export function CoachMark({
  children,
  className,
  content,
  delayMs = 500,
  flowId,
  onComplete,
  onCloseAutoFocus,
  onInteractOutside,
  onOpenAutoFocus,
  show = true,
  sideOffset = 10,
  ...contentProps
}: CoachMarkProps) {
  const { user, isLoaded } = useUserContext();
  const { mutate: completeOnboardingFlow } = useCompleteOnboardingFlow();
  const [completedInThisSession, setCompletedInThisSession] = useState(() => new Set<OnboardingFlow>());
  const [hasDelayElapsed, setHasDelayElapsed] = useState(false);

  const isComplete = completedInThisSession.has(flowId) || user?.completedOnboardingFlows.includes(flowId);
  const isEligible = show && isLoaded && !!user && !isComplete;
  const isOpen = isEligible && hasDelayElapsed;

  useEffect(() => {
    setHasDelayElapsed(false);
    if (!isEligible) return;

    const timeout = window.setTimeout(() => setHasDelayElapsed(true), delayMs);
    return () => window.clearTimeout(timeout);
  }, [delayMs, isEligible]);

  const complete = () => {
    if (completedInThisSession.has(flowId)) return;

    setCompletedInThisSession((completedFlows) => new Set(completedFlows).add(flowId));
    completeOnboardingFlow(flowId);
    onComplete?.();
  };

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && complete()}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        sideOffset={sideOffset}
        disableScrollLock
        onOpenAutoFocus={onOpenAutoFocus ?? ((event) => event.preventDefault())}
        onCloseAutoFocus={onCloseAutoFocus ?? ((event) => event.preventDefault())}
        onInteractOutside={onInteractOutside ?? ((event) => event.preventDefault())}
        className={cn('flex w-96 max-w-none flex-col rounded-xl p-2 text-[13px]', className)}
        {...contentProps}
      >
        <div className="px-1.75 py-0.5 text-gray-800">{content}</div>

        <Button onClick={complete} className="self-end text-xs" size="sm" variant="ghost">
          <Icon className="lucide--check" />
          Ok
        </Button>

        <PopoverArrow asChild width={14} height={6}>
          <svg className="overflow-visible" aria-hidden="true">
            <path
              d="M 0 -2 L 15 10 L 30 -2"
              className="fill-white stroke-gray-200"
              strokeWidth="2"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </PopoverArrow>
      </PopoverContent>
    </Popover>
  );
}
